// «Resolver» un pago informado por la pasarela que no se pudo atribuir a una
// suscripcion. No mueve plata: se usa cuando el operador ya identifico la
// cuenta (y el pago se acredito por el camino normal) o determino que no hay
// nada que hacer. Mientras siga abierto, el invariante lo cuenta y alarma.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { adminResolveUnmatchedPayment, AdminApiError } from '@/lib/api';
import { canMarkRefunded } from '@/lib/access';
import { useSession } from '@/hooks/useSession';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls } from '@/lib/form-styles';

const MAX = 500;

export default function ResolveUnmatchedPaymentButton({
  id,
  paymentId,
}: {
  id: string;
  paymentId: string;
}): JSX.Element {
  const session = useSession();
  const allowed = canMarkRefunded(session);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [touched, setTouched] = useState(false);

  const trimmed = resolution.trim();
  const problem =
    trimmed.length < 3
      ? 'Escribí con qué criterio se resuelve (mínimo 3 caracteres).'
      : trimmed.length > MAX
        ? `Máximo ${MAX} caracteres.`
        : null;

  const mut = useMutation({
    mutationFn: () => adminResolveUnmatchedPayment(id, trimmed),
    onSuccess: () => {
      setOpen(false);
      setResolution('');
      setTouched(false);
      void qc.invalidateQueries({ queryKey: ['admin-pending-charges'] });
    },
  });

  return (
    <>
      <button
        type="button"
        data-help="charges.resolve-unmatched"
        onClick={() => {
          mut.reset();
          setOpen(true);
        }}
        disabled={!allowed}
        title={allowed ? undefined : 'Requiere Usuarios en nivel completo y el permiso danger_zone'}
        className="min-h-[2rem] px-2.5 text-xs inline-flex items-center gap-1.5 rounded-lg font-medium border border-surface-border dark:border-surface-dark-border hover:bg-surface-hover dark:hover:bg-surface-dark-hover disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-brand"
      >
        <CheckCircle2 size={14} aria-hidden="true" />
        Resolver
      </button>

      <ConfirmDialog
        open={open}
        title="Resolver pago no identificado"
        description={
          <div className="space-y-3">
            <p>
              Pago <span className="font-mono">{paymentId}</span> de la pasarela. Deja de contar en
              la alerta cuando confirmes el criterio.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>No mueve plata: si hay que reintegrar, eso se hace aparte con la pasarela.</li>
              <li>El motivo queda en el audit junto al id del pago.</li>
            </ul>
            <label className="block text-xs">
              <span className="font-medium">Motivo / a qué cuenta se acreditó</span>
              <textarea
                value={resolution}
                maxLength={MAX}
                onChange={(e) => setResolution(e.target.value)}
                onBlur={() => setTouched(true)}
                rows={3}
                aria-invalid={touched && problem ? true : undefined}
                className={fieldCls({ extra: 'mt-1', invalid: Boolean(touched && problem) })}
              />
            </label>
            {touched && problem ? <p className="text-status-error text-xs">{problem}</p> : null}
            {mut.isError ? (
              <p className="text-status-error text-xs">
                Error: {mut.error instanceof AdminApiError ? mut.error.message : 'desconocido'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Resolver"
        loading={mut.isPending}
        onConfirm={() => {
          setTouched(true);
          if (problem) return;
          mut.mutate();
        }}
        onCancel={() => {
          setOpen(false);
          mut.reset();
        }}
      />
    </>
  );
}
